import Axios from 'axios';

Axios.defaults.xsrfCookieName = 'csrftoken';
Axios.defaults.xsrfHeaderName = 'X-CSRFToken';
Axios.defaults.headers.common['X-Accept'] = 'application/json';

/**
 * A service class for handling multimedia files in a press release.
 */
export default class MultimediaService {
  baseUrl = '/api/v3/pressrelease';

  multimediaEndpoint = 'multimedia-add-on';

  /**
   * Creates an instance of MultimediaService.
   * @param {number} pressReleaseId - The ID of the press release.
   */
  constructor(pressReleaseId) {
    this.pressReleaseId = pressReleaseId;
  }

  /**
   * Generates the URL for the multimedia endpoint.
   * @param {number} [addOnId=null] - The ID of the multimedia add-on.
   * @returns {string} The generated URL.
   */
  generateUrl(addOnId = null) {
    let url = `${this.baseUrl}/${this.pressReleaseId}/${this.multimediaEndpoint}/`;
    if (addOnId) {
      url += `${addOnId}/`;
    }
    return url;
  }

  /**
   * Retrieves all multimedia files for the press release.
   * @returns {Promise<Array>} A promise that resolves to an array of multimedia files.
   */
  async getFiles() {
    const url = this.generateUrl();
    const response = await Axios.get(url);
    return response.data;
  }

  /**
   * Uploads an array of files to the multimedia endpoint.
   * @param {Array<File>} fileArray - The array of files to upload.
   * @returns {Promise<Array>} A promise that resolves to an array of uploaded files.
   */
  async uploadFiles(fileArray) {
    const formData = new FormData();
    fileArray.forEach((file) => {
      formData.append(file.name, file);
    });
    const url = this.generateUrl();
    const response = await Axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Uploads an embed URL to the multimedia endpoint.
   * @param {string} embedUrl - The embed URL to upload.
   * @returns {Promise<Object>} A promise that resolves to the uploaded multimedia file.
   */
  async uploadEmbedUrl(embedUrl) {
    const url = this.generateUrl();
    const response = await Axios.post(`${url}?embed=True`, {
      embed_url: embedUrl,
    });
    return response.data;
  }

  /**
   * Updates the caption of a multimedia file.
   * @param {number} addOnId - The ID of the multimedia add-on.
   * @param {string} caption - The new caption for the multimedia file.
   * @returns {Promise<Object>} A promise that resolves to the updated multimedia file.
   */
  async updateCaption(addOnId, caption) {
    const url = this.generateUrl(addOnId);
    const response = await Axios.put(url, {
      caption,
    });
    return response.data;
  }

  /**
   * Deactivates by removing the Cloudinary asset and setting the record to active=False.
   * @param {number} addOnId - The ID of the multimedia add-on.
   * @returns {Promise<Object>} A promise that resolves to the deactivated multimedia file.
   */
  async deactivateFile(addOnId) {
    const url = this.generateUrl(addOnId);
    const response = await Axios.delete(`${url}?keep_record=true`);
    return response.data;
  }

  /**
   * Deletes both the Cloudinary asset and the database record. Used to prevent orphaned records.
   * @param {number} addOnId - The ID of the multimedia add-on.
   * @returns {Promise<Object>} A promise that resolves to the deleted multimedia file.
   */
  async deleteFile(addOnId) {
    const url = this.generateUrl(addOnId);
    const response = await Axios.delete(url);
    return response.data;
  }

  /**
   * Deactivates all multimedia files for the press release.
   * @returns {Promise<Object>} A promise that resolves to the deactivated multimedia files.
   */
  async deactivateAllFiles() {
    const url = this.generateUrl();
    const response = await Axios.delete(`${url}?keep_record=true`);
    return response.data;
  }

  /**
   * Deletes all multimedia files for the press release.
   * @returns {Promise<Object>} A promise that resolves to the deleted multimedia files.
   */
  async deleteAllFiles() {
    const url = this.generateUrl();
    const response = await Axios.delete(url);
    return response.data;
  }
}
